import { authenticatedFetch } from './authenticatedFetch';
import { apiUrl } from '../config/appConfig';

/**
 * PATIENT INTAKE API - Fetch and update patient intake records (patient_intake table).
 */

export interface PatientIntake {
  id?: number;
  pat_id: number;
  full_name?: string | null;
  date_of_birth?: string | null;
  sex_at_birth?: string | null;
  phone?: string | null;
  email?: string | null;
  preferred_language?: string | null;
  emergency_contact_name?: string | null;
  emergency_contact_phone?: string | null;
  reason?: string | null;
  complaint?: string | null;
  symptom_chest_pain?: boolean | null;
  symptom_shortness_breath?: boolean | null;
  symptom_wheeze?: boolean | null;
  symptom_cough?: boolean | null;
  symptom_fever?: boolean | null;
  symptom_palpitations?: boolean | null;
  symptom_swelling_legs?: boolean | null;
  symptom_dizziness?: boolean | null;
  symptom_none?: boolean | null;
  hypertension?: boolean | null;
  high_cholesterol?: boolean | null;
  cad?: boolean | null;
  heart_failure?: boolean | null;
  arrhythmia?: boolean | null;
  stroke?: boolean | null;
  asthma?: boolean | null;
  copd?: boolean | null;
  sleep_apnea?: boolean | null;
  chronic_bronchitis?: boolean | null;
  other_lung_disease?: string | null;
  diabetes?: boolean | null;
  ckd?: boolean | null;
  thyroid?: boolean | null;
  obesity?: boolean | null;
  depression_anxiety?: boolean | null;
  other_condition?: string | null;
  smoking_status?: string | null;
  takes_meds?: boolean | null;
  medications?: string | null;
  has_allergies?: boolean | null;
  allergies?: string | null;
  bp_cuff?: boolean | null;
  pulse_ox?: boolean | null;
  scale?: boolean | null;
  inhaler?: boolean | null;
  wearable?: boolean | null;
  wearable_brand?: string | null;
  consent?: boolean | null;
  created_at?: string | null;
}

const patientIntakeApiUrl = (path: string) => apiUrl(path);

function parseJsonTextSafely(text: string, fallbackMessage: string): any {
  try {
    return JSON.parse(text);
  } catch {
    if (text.trim().startsWith('<')) {
      throw new Error('Server returned HTML instead of JSON');
    }
    throw new Error(fallbackMessage);
  }
}

async function parseErrorResponse(response: Response): Promise<string> {
  const text = await response.text();
  if (!text) return 'Request failed';
  try {
    const j = JSON.parse(text);
    return (j && j.error) || text;
  } catch {
    return text || 'Request failed';
  }
}

export async function getPatientIntake(patId: string): Promise<PatientIntake | null> {
  const q = new URLSearchParams({ pat_id: String(patId) });
  const response = await authenticatedFetch(patientIntakeApiUrl(`/patient-intake?${q.toString()}`));
  if (response.status === 404) return null;
  if (!response.ok) throw new Error(await parseErrorResponse(response));
  const text = await response.text();
  if (!text) throw new Error('Empty response');
  return parseJsonTextSafely(text, 'Invalid patient intake response');
}

export async function updatePatientIntake(patId: string, data: Partial<PatientIntake>): Promise<PatientIntake> {
  const q = new URLSearchParams({ pat_id: String(patId) });
  const response = await authenticatedFetch(patientIntakeApiUrl(`/patient-intake?${q.toString()}`), {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error(await parseErrorResponse(response));
  const text = await response.text();
  if (!text) throw new Error('Empty response');
  return parseJsonTextSafely(text, 'Invalid patient intake update response');
}

export async function createPatientIntake(patId: string, data: Partial<PatientIntake>): Promise<PatientIntake> {
  const q = new URLSearchParams({ pat_id: String(patId) });
  const response = await authenticatedFetch(patientIntakeApiUrl(`/patient-intake?${q.toString()}`), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(data),
  });
  if (!response.ok) throw new Error(await parseErrorResponse(response));
  const text = await response.text();
  if (!text) throw new Error('Empty response');
  return parseJsonTextSafely(text, 'Invalid patient intake create response');
}
